/* globals gform, google */

import { requestGoogleMaps } from './lazy-gmaps';

const SELECTOR = '.gfield.address-autocomplete input[type="text"]';
const INITIALIZED = 'data-places-autocomplete';

/**
 * Attach Google Places Autocomplete to Gravity Forms address text fields.
 * Mark fields with CSS class `address-autocomplete` in the form editor.
 * On place select, fills admin-label fields: street-housenumber, city, zipcode.
 * Maps is lazy-loaded on focus.
 */

function getAddressInputs(scope) {
	const root = scope || document;

	return Array.from(root.querySelectorAll(SELECTOR));
}

function getFormFromInput(input) {
	return input.closest('form') || document;
}

function getInputByAdminLabel(form, adminLabel) {
	const field = form.querySelector(`.gfield-admin-label-${adminLabel}`);

	if (!field) {
		return null;
	}

	return field.querySelector('input[type="text"], input:not([type]), textarea');
}

function setFieldValue(input, value) {
	if (!input || value === undefined || value === null) {
		return;
	}

	input.value = value;
	input.dispatchEvent(new Event('input', { bubbles: true }));
	input.dispatchEvent(new Event('change', { bubbles: true }));
}

function getAddressComponent(components, type) {
	if (!components || !components.length) {
		return '';
	}

	const match = components.find((component) => {
		return component.types.includes(type);
	});

	if (!match) {
		return '';
	}

	return match.long_name || '';
}

function parsePlaceAddress(place) {
	const components = place?.address_components || [];
	const streetNumber = getAddressComponent(components, 'street_number');
	const route = getAddressComponent(components, 'route');
	const postalCode = getAddressComponent(components, 'postal_code');
	let city = getAddressComponent(components, 'locality');

	if (!city) {
		city = getAddressComponent(components, 'postal_town');
	}

	if (!city) {
		city = getAddressComponent(components, 'administrative_area_level_2');
	}

	let streetHousenumber = '';

	if (route && streetNumber) {
		streetHousenumber = `${route} ${streetNumber}`;
	} else if (route) {
		streetHousenumber = route;
	} else if (streetNumber) {
		streetHousenumber = streetNumber;
	}

	return {
		streetHousenumber,
		city,
		zipcode: postalCode,
	};
}

function fillAddressFields(form, place) {
	const parsed = parsePlaceAddress(place);

	setFieldValue(getInputByAdminLabel(form, 'street-housenumber'), parsed.streetHousenumber);
	setFieldValue(getInputByAdminLabel(form, 'city'), parsed.city);
	setFieldValue(getInputByAdminLabel(form, 'zipcode'), parsed.zipcode);
}

function initAutocompleteOnInput(input) {
	if (!input || input.getAttribute(INITIALIZED) === '1') {
		return;
	}

	if (typeof google === 'undefined' || !google.maps?.places?.Autocomplete) {
		return;
	}

	const autocomplete = new google.maps.places.Autocomplete(input, {
		types: ['address'],
		componentRestrictions: {
			country: 'nl',
		},
		fields: ['address_components', 'formatted_address'],
	});

	autocomplete.addListener('place_changed', () => {
		const place = autocomplete.getPlace();
		const form = getFormFromInput(input);

		fillAddressFields(form, place);
	});

	input.setAttribute(INITIALIZED, '1');
}

document.addEventListener('focusin', (event) => {
	const input = event.target;

	if (!input?.matches?.(SELECTOR)) {
		return;
	}

	if (input.getAttribute(INITIALIZED) === '1') {
		return;
	}

	requestGoogleMaps(() => {
		initAutocompleteOnInput(input);
	});
});

document.addEventListener('gform/postRender', (event) => {
	if (typeof gform === 'undefined' || !gform.utils) {
		return;
	}

	if (typeof google === 'undefined' || !google.maps?.places?.Autocomplete) {
		return;
	}

	const form = gform.utils.getNode(`#gform_${event.detail.formId}`, document, true);
	const inputs = getAddressInputs(form);

	inputs.forEach((input) => {
		initAutocompleteOnInput(input);
	});
});
